import { useState, useEffect } from "react";
import { fetchCallRating } from "@services/callRatings";

const SCORE_COLORS = {
  high: { bg: "bg-green-100", text: "text-green-700", bar: "bg-green-500" },
  medium: { bg: "bg-yellow-100", text: "text-yellow-700", bar: "bg-yellow-500" },
  low: { bg: "bg-red-100", text: "text-red-700", bar: "bg-red-500" },
};

const getScoreColor = (score) => {
  if (score >= 4) return SCORE_COLORS.high;
  if (score >= 3) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
};

const DIMENSION_LABELS = {
  question_compliance: "Question Compliance",
  rule_compliance: "Rule Compliance",
  information_accuracy: "Information Accuracy",
  goal_achievement: "Goal Achievement",
  conversation_quality: "Conversation Quality",
  error_handling: "Error Handling",
};

const OUTCOME_LABELS = {
  APPOINTMENT_BOOKED: { label: "Appointment Booked", color: "bg-green-100 text-green-700" },
  APPOINTMENT_RESCHEDULED: { label: "Rescheduled", color: "bg-blue-100 text-blue-700" },
  APPOINTMENT_CANCELLED: { label: "Cancelled", color: "bg-orange-100 text-orange-700" },
  QUESTION_ANSWERED: { label: "Question Answered", color: "bg-purple-100 text-purple-700" },
  MESSAGE_TAKEN: { label: "Message Taken", color: "bg-indigo-100 text-indigo-700" },
  TRANSFERRED: { label: "Transferred", color: "bg-cyan-100 text-cyan-700" },
  ABANDONED: { label: "Abandoned", color: "bg-red-100 text-red-700" },
  UNKNOWN: { label: "Unknown", color: "bg-gray-100 text-gray-700" },
};

function ScoreBar({ score, label }) {
  const colors = getScoreColor(score);
  const percentage = Math.round((score / 5) * 100);

  return (
    <div className="flex items-center gap-3">
      <div className="w-44 text-sm text-gray-600 whitespace-nowrap">
        {label}
      </div>
      <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${colors.bar} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className={`w-12 text-right text-sm font-semibold ${colors.text}`}>
        {percentage}%
      </div>
    </div>
  );
}

export default function CallRatingModal({
  isOpen,
  onClose,
  conversationId,
  patientName,
}) {
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && conversationId) {
      loadRating();
    }
  }, [isOpen, conversationId]);

  const loadRating = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCallRating(conversationId);
      setRating(res?.data || null);
    } catch (err) {
      setError(err?.message || "Failed to load rating");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const overallScore = rating?.overall_score;
  const overallColors = overallScore ? getScoreColor(overallScore) : null;
  const outcomeConfig = OUTCOME_LABELS[rating?.call_outcome] || OUTCOME_LABELS.UNKNOWN;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="rating-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={handleBackdropClick}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-5">
            <div className="flex items-start justify-between">
              <div>
                <h3
                  id="rating-modal-title"
                  className="text-lg font-semibold text-white"
                >
                  Call Rating Details
                </h3>
                <p className="mt-1 text-sm text-indigo-100">
                  {patientName || "Unknown Caller"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-indigo-100 hover:bg-white/10 hover:text-white transition-colors"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-10 w-10 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
                  <span className="text-sm text-gray-500">Loading rating...</span>
                </div>
              </div>
            ) : error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={loadRating}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            ) : !rating ? (
              <div className="text-center py-12">
                <div className="rounded-full bg-gray-100 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg
                    className="h-8 w-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500">No rating available for this call</p>
                <p className="text-xs text-gray-400 mt-1">
                  Rating will be generated automatically within a few minutes
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* CRITICAL: Opening Hours Violations */}
                {rating.opening_hours_violations?.length > 0 && (
                  <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="rounded-full bg-red-500 p-1 flex-shrink-0">
                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-red-900 mb-2">
                          🚨 Opening Hours Violation Detected
                        </h4>
                        <div className="space-y-2">
                          {rating.opening_hours_violations.map((violation, idx) => (
                            <div key={idx} className="bg-white rounded p-2 border border-red-200">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded">
                                  {violation.severity}
                                </span>
                                <span className="text-xs text-gray-500 capitalize">
                                  {violation.type.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <p className="text-sm text-red-900">{violation.description}</p>
                            </div>
                          ))}
                        </div>
                        {rating.opening_hours_analysis?.appointment_details && (
                          <div className="mt-3 text-xs text-gray-700 bg-white rounded p-2 border border-red-200">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="font-semibold">Attempted:</span>{' '}
                                {rating.opening_hours_analysis.appointment_details.day}{' '}
                                {rating.opening_hours_analysis.appointment_details.time && 
                                  `at ${rating.opening_hours_analysis.appointment_details.time}`}
                              </div>
                              <div>
                                <span className="font-semibold">Clinic Hours:</span>{' '}
                                {rating.opening_hours_analysis.clinic_hours_for_day || 'N/A'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Review Required */}
                {rating.requires_review && !rating.reviewed_at && (
                  <div className="rounded-lg border border-orange-300 bg-orange-50 p-4">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-orange-900 mb-1">
                          ⚠️ Requires Human Review
                        </h4>
                        {rating.review_reasons?.length > 0 ? (
                          <ul className="space-y-1 mt-2">
                            {rating.review_reasons.map((reason, idx) => (
                              <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                                <span className="text-orange-400 mt-0.5">•</span>
                                <span>{reason}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-orange-700">
                            This call has been flagged for human review
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Overall Score */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Overall Score</div>
                    <div className="flex items-center gap-1">
                      <span
                        className={`text-4xl font-bold ${overallColors?.text}`}
                      >
                        {Math.round((overallScore / 5) * 100)}
                      </span>
                      <span className="text-2xl text-gray-400">%</span>
                    </div>
                    {rating.opening_hours_violations?.length > 0 && (
                      <p className="text-xs text-red-600 mt-1">Score capped due to violations</p>
                    )}
                  </div>
                  <div
                    className={`px-4 py-2 rounded-full text-sm font-medium ${outcomeConfig.color}`}
                  >
                    {outcomeConfig.label}
                  </div>
                </div>

                {/* Dimension Scores */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Dimension Scores
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
                      <ScoreBar
                        key={key}
                        score={rating.scores?.[key] || 0}
                        label={label}
                      />
                    ))}
                  </div>
                </div>

                {/* Question Compliance */}
                {(rating.questions_analysis?.length > 0 || rating.questions_asked?.length > 0 || rating.questions_missed?.length > 0) && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Question Compliance</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                          ✓ Obtained: {rating.questions_asked?.length || 0}
                        </span>
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                          ✗ Missing: {rating.questions_missed?.length || 0}
                        </span>
                      </div>
                    </div>
                    
                    {rating.questions_analysis?.length > 0 ? (
                      <div className="space-y-2">
                        {rating.questions_analysis.map((q, idx) => {
                          const obtained = q.information_obtained ?? q.was_asked ?? false;
                          return (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg border-l-4 ${
                                obtained
                                  ? 'bg-green-50 border-l-green-500'
                                  : 'bg-red-50 border-l-red-500'
                              }`}
                            >
                              <div className="flex items-start gap-2 mb-1">
                                <span className="mt-0.5">
                                  {obtained ? (
                                    <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  )}
                                </span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className={`text-sm font-medium ${
                                      obtained ? 'text-green-900' : 'text-red-900'
                                    }`}>
                                      {q.question}
                                    </p>
                                    {q.criticality && (
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        q.criticality === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                                        q.criticality === 'IMPORTANT' ? 'bg-orange-100 text-orange-700' :
                                        'bg-blue-100 text-blue-700'
                                      }`}>
                                        {q.criticality}
                                      </span>
                                    )}
                                    {q.how_obtained && obtained && (
                                      <span className={`text-xs px-2 py-0.5 rounded border ${
                                        q.how_obtained === 'volunteered_by_caller'
                                          ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                                          : 'bg-gray-50 text-gray-700 border-gray-200'
                                      }`}>
                                        {q.how_obtained === 'volunteered_by_caller' ? '💡 Volunteered' : '❓ Asked'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {q.evidence && (
                                <div className="mt-2 pl-6 text-xs text-gray-600 bg-white rounded p-2 border border-gray-200">
                                  <em>Evidence: {q.evidence}</em>
                                </div>
                              )}
                              {q.notes && (
                                <div className="mt-2 pl-6 text-xs text-blue-700 bg-blue-50 rounded p-2 border border-blue-200">
                                  💡 {q.notes}
                                </div>
                              )}
                              {!q.rules_followed && obtained && (
                                <div className="mt-2 pl-6 flex items-center gap-1 text-xs text-orange-600">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  Rules not followed for this information
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {rating.questions_asked?.map((q, idx) => (
                          <div key={idx} className="text-sm text-green-700 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            {q}
                          </div>
                        ))}
                        {rating.questions_missed?.map((q, idx) => (
                          <div key={idx} className="text-sm text-red-700 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {q}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Prompt Compliance Analysis */}
                {rating.prompt_compliance_analysis && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Prompt Compliance</h4>
                    
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-700">
                          {rating.prompt_compliance_analysis.key_rules_from_prompt?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Total Rules</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">
                          {rating.prompt_compliance_analysis.rules_followed?.length || 0}
                        </div>
                        <div className="text-xs text-green-600 mt-1">Followed</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-700">
                          {rating.prompt_compliance_analysis.rules_violated?.length || 0}
                        </div>
                        <div className="text-xs text-red-600 mt-1">Violated</div>
                      </div>
                    </div>

                    {rating.prompt_compliance_analysis.violation_details?.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-gray-600 mb-2">Rule Violations:</h5>
                        <div className="space-y-2">
                          {rating.prompt_compliance_analysis.violation_details.map((detail, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                              <svg className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="text-sm text-orange-900">{detail}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Strengths */}
                {rating.strengths?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                        <svg
                          className="h-3 w-3 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                      Strengths
                    </h4>
                    <ul className="space-y-1.5">
                      {rating.strengths.map((item, i) => (
                        <li
                          key={i}
                          className="text-sm text-gray-600 pl-7 relative before:content-[''] before:absolute before:left-2 before:top-2 before:w-1.5 before:h-1.5 before:bg-green-400 before:rounded-full"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Issues - Grouped by Severity */}
                {rating.issues_found?.length > 0 && (
                  <div>
                    {(() => {
                      const criticalIssues = rating.issues_found.filter(i => 
                        i.includes('[CRITICAL]') || i.includes('CRITICAL')
                      );
                      const highIssues = rating.issues_found.filter(i => 
                        (i.includes('[HIGH]') || i.includes('Opening hours')) && 
                        !criticalIssues.includes(i)
                      );
                      const normalIssues = rating.issues_found.filter(i => 
                        !criticalIssues.includes(i) && !highIssues.includes(i)
                      );

                      return (
                        <>
                          {criticalIssues.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                                <span className="h-5 w-5 rounded-full bg-red-600 flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                  </svg>
                                </span>
                                🚨 Critical Issues
                              </h4>
                              <div className="space-y-2">
                                {criticalIssues.map((item, i) => (
                                  <div key={i} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-900 font-medium">{item}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {highIssues.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
                                <span className="h-5 w-5 rounded-full bg-orange-500 flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </span>
                                ⚠️ High Priority Issues
                              </h4>
                              <div className="space-y-2">
                                {highIssues.map((item, i) => (
                                  <div key={i} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-sm text-orange-900">{item}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {normalIssues.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <span className="h-5 w-5 rounded-full bg-gray-400 flex items-center justify-center">
                                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                  </svg>
                                </span>
                                Issues Found
                              </h4>
                              <ul className="space-y-1.5">
                                {normalIssues.map((item, i) => (
                                  <li key={i} className="text-sm text-gray-600 pl-7 relative before:content-[''] before:absolute before:left-2 before:top-2 before:w-1.5 before:h-1.5 before:bg-gray-400 before:rounded-full">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Suggestions */}
                {rating.improvement_suggestions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                          />
                        </svg>
                      </span>
                      Improvement Suggestions
                    </h4>
                    <ul className="space-y-1.5">
                      {rating.improvement_suggestions.map((item, i) => (
                        <li
                          key={i}
                          className="text-sm text-gray-600 pl-7 relative before:content-[''] before:absolute before:left-2 before:top-2 before:w-1.5 before:h-1.5 before:bg-blue-400 before:rounded-full"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* GPT Reasoning */}
                {rating.gpt_reasoning && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      AI Analysis
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {rating.gpt_reasoning}
                    </p>
                  </div>
                )}

                {/* Meta info */}
                <div className="border-t border-gray-100 pt-4 flex items-center justify-between text-xs text-gray-400">
                  <span>Model: {rating.rating_model || "N/A"}</span>
                  <span>
                    Rated:{" "}
                    {rating.rated_at
                      ? new Date(rating.rated_at).toLocaleString()
                      : "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end border-t border-gray-100 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
